import { Component } from '@theme/component';

/**
 * Silent product demonstrations that loop while on screen. Posters remain visible when
 * reduced motion is requested or autoplay is unavailable.
 * A video starts downloading a little before it scrolls into view (NEAR_MARGIN), so it plays as soon as it
 * appears; before, its download only started once 15% of it was on screen.
 * Stillwell (r7):
 * - One video plays at a time, the one most on screen: two loops side by side shared a slow connection and both froze.
 * - Each frame has a Pause / Play button (WCAG 2.2.2: moving content longer than 5s can be paused); a video the
 *   shopper paused stays paused until they press Play.
 * Stillwell (r8): a pause also stops the block's autoplay until the shopper presses Play on one of its videos (pausing
 *   one video started the next one on screen at once, and it kept playing after scrolling away and back).
 * - On data saver or a connection under 2 Mbps nothing downloads by itself: the poster shows with the native play
 *   button, as with reduced motion (the 0.4-0.8MB loops stalled 3-11 times in 10s on a 1.6 Mbps phone).
 * @extends {Component<{ videos: HTMLVideoElement[], toggles: HTMLButtonElement[] }>}
 */
/** How far ahead of the screen a video starts downloading. */
const NEAR_MARGIN = '600px 0px';
/** Share of a video that must be on screen before it plays. */
const MIN_RATIO = 0.15;

class SlVideoStory extends Component {
  #observer;
  #nearObserver;
  #controller;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {Map<Element, number>} visible share of each video */
  #ratio = new Map();
  /** Stillwell: videos whose autoplay the browser refused (iOS Low Power Mode, data saver) */
  #blocked = new WeakSet();
  /** Videos the shopper paused with the button */
  #held = new WeakSet();
  /** @type {HTMLVideoElement | null} the video the shopper last started with the button */
  #chosen = null;
  /** Stillwell: the shopper paused a video of this block: nothing plays by itself until they press Play */
  #stopped = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #setup() {
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#motion.addEventListener('change', this.#sync, options);
    document.addEventListener('visibilitychange', this.#sync, options);
    document.addEventListener('sl:media-retry', () => setTimeout(this.#sync, 300), options);
    const connection = /** @type {any} */ (navigator).connection;
    connection?.addEventListener?.('change', this.#sync, options);
    for (const video of this.refs.videos ?? []) {
      video.addEventListener('play', () => this.#label(video), options);
      video.addEventListener('pause', () => this.#label(video), options);
    }
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver((entries) => {
        for (const entry of entries) this.#ratio.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        this.#sync();
      }, { threshold: [0, MIN_RATIO, 0.35, 0.55, 0.75, 0.95] });
      for (const video of this.refs.videos ?? []) this.#observer.observe(video);
      this.#nearObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#poster(/** @type {HTMLVideoElement} */ (entry.target));
          if (!this.#manual(/** @type {HTMLVideoElement} */ (entry.target))) this.#load(/** @type {HTMLVideoElement} */ (entry.target));
          this.#nearObserver?.unobserve(entry.target);
        }
      }, { rootMargin: NEAR_MARGIN });
      for (const video of this.refs.videos ?? []) this.#nearObserver.observe(video);
    } else {
      for (const video of this.refs.videos ?? []) { this.#poster(video); this.#ratio.set(video, 1); }
    }
    this.#sync();
  }

  /**
   * Sets the poster (data-poster) once the video is near the screen: posters 8,000-10,000px down the page used to
   * download with the first screen.
   * @param {HTMLVideoElement} video
   */
  #poster(video) {
    if (video.dataset.poster && !video.getAttribute('poster')) video.poster = video.dataset.poster;
  }

  /** Data saver, 2G, or a measured downlink under 2 Mbps (browsers without navigator.connection: not slow). */
  #slow() {
    const c = /** @type {any} */ (navigator).connection;
    return !!c && (c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '') || (typeof c.downlink === 'number' && c.downlink > 0 && c.downlink < 2));
  }

  /**
   * Manual mode: nothing plays by itself, the native controls show and the file loads only when play is pressed.
   * @param {HTMLVideoElement} video
   */
  #manual(video) {
    return this.#motion.matches || this.#blocked.has(video) || this.#slow();
  }

  #sync = () => {
    const videos = this.refs.videos ?? [];
    // The video to play: the one the shopper started, while it is on screen; else the one most on screen
    let best = null;
    if (!document.hidden) {
      if (this.#chosen && (this.#ratio.get(this.#chosen) ?? 0) > 0) best = this.#chosen;
      else if (!this.#stopped) {
        let top = 0;
        for (const video of videos) {
          const ratio = this.#ratio.get(video) ?? 0;
          if (ratio < MIN_RATIO || this.#held.has(video) || this.#manual(video)) continue;
          if (ratio > top + 0.01) { top = ratio; best = video; }
        }
      }
    }
    for (const video of videos) {
      video.muted = true;
      video.defaultMuted = true;
      const manual = this.#manual(video);
      video.controls = manual;
      const toggle = this.#toggleFor(video);
      if (toggle) toggle.hidden = manual;
      const visible = (this.#ratio.get(video) ?? 0) >= MIN_RATIO;
      if (document.hidden || !visible) {
        video.pause();
        continue;
      }
      if (manual) {
        this.#ready(video);
        continue;
      }
      if (video !== best) {
        video.pause();
        continue;
      }
      this.#load(video);
      if (video.paused) {
        video.play().catch((error) => {
          // Only a refusal (NotAllowedError) means autoplay is blocked; a failed or interrupted load is retried by
          // snippets/sw-head.liquid when the connection is back
          if (error?.name !== 'NotAllowedError') return;
          this.#blocked.add(video);
          if (this.#chosen === video) this.#chosen = null;
          this.#sync();
        });
      }
    }
  };

  /**
   * Pause / Play button of a frame.
   * @param {MouseEvent} event
   */
  toggle(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const video = target.closest('.sl-video-story__frame')?.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.paused) {
      this.#held.delete(video);
      this.#chosen = video;
      this.#stopped = false;
    } else {
      this.#held.add(video);
      if (this.#chosen === video) this.#chosen = null;
      this.#stopped = true;
      video.pause();
    }
    this.#sync();
  }

  /** @param {HTMLVideoElement} video */
  #toggleFor(video) {
    const button = video.closest('.sl-video-story__frame')?.querySelector('.sl-video-story__toggle');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** @param {HTMLVideoElement} video */
  #label(video) {
    const button = this.#toggleFor(video);
    if (!button) return;
    const playing = !video.paused;
    button.dataset.state = playing ? 'playing' : 'paused';
    const label = playing ? button.dataset.pause : button.dataset.play;
    if (label) button.setAttribute('aria-label', label);
  }

  /**
   * Stillwell: gives the video its source without buffering it (manual mode: the native play button needs a source).
   * @param {HTMLVideoElement} video
   */
  #ready(video) {
    if (video.hasAttribute('src') || !video.dataset.src) return;
    video.preload = 'none';
    video.src = video.dataset.src;
  }

  /**
   * Gives the video its source and lets the browser buffer it.
   * @param {HTMLVideoElement} video
   */
  #load(video) {
    if (this.#manual(video) || video.hasAttribute('src') || !video.dataset.src) return;
    video.preload = 'auto';
    video.src = video.dataset.src;
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#nearObserver?.disconnect();
    for (const video of this.refs.videos ?? []) video.pause();
    this.#ratio.clear();
    this.#chosen = null;
  }
}

if (!customElements.get('sl-video-story')) customElements.define('sl-video-story', SlVideoStory);
